import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  CardHeader,
  CardBody,
  Code,
  Alert,
  AlertIcon,
  List,
  ListItem,
  ListIcon,
  OrderedList,
  useColorModeValue,
  Divider,
  Badge,
  SimpleGrid,
  Icon,
} from '@chakra-ui/react';
import {
  CheckCircleIcon,
  InfoIcon,
  WarningIcon,
  ViewIcon,
  EditIcon,
  AttachmentIcon,
  TimeIcon,
} from '@chakra-ui/icons';
import DocumentationLayout from '../../components/DocumentationLayout';

const FirstProblem: React.FC = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textSecondary = useColorModeValue('gray.600', 'gray.400');
  const codeBg = useColorModeValue('gray.100', 'gray.700');
  const accentBg = useColorModeValue('blue.50', 'blue.900');

  const StepCard: React.FC<{
    step: number;
    title: string;
    children: React.ReactNode;
  }> = ({ step, title, children }) => (
    <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
      <CardHeader>
        <HStack>
          <Badge colorScheme="blue" borderRadius="full" px={3}>
            Step {step}
          </Badge>
          <Heading size="md">{title}</Heading>
        </HStack>
      </CardHeader>
      <CardBody pt={0}>{children}</CardBody>
    </Card>
  );

  const exampleCode = `#include <iostream>
#include <vector>
#include <cilk/cilk.h>

// Basic parallel sum implementation
long long parallel_sum(const std::vector<int>& data) {
    size_t n = data.size();
    if (n <= 1000) {
        // Base case: sequential sum for small arrays
        long long sum = 0;
        for (int x : data) {
            sum += x;
        }
        return sum;
    }
    
    // Parallel divide and conquer
    size_t mid = n / 2;
    std::vector<int> left(data.begin(), data.begin() + mid);
    std::vector<int> right(data.begin() + mid, data.end());
    
    long long left_sum, right_sum;
    
    // Use cilk_spawn for parallel execution
    left_sum = cilk_spawn parallel_sum(left);
    right_sum = parallel_sum(right);
    cilk_sync; // Wait for spawned task to complete
    
    return left_sum + right_sum;
}

int main() {
    // Read input size
    int n;
    std::cin >> n;
    
    // Read input data
    std::vector<int> data(n);
    for (int i = 0; i < n; i++) {
        std::cin >> data[i];
    }
    
    // Compute and output result
    long long result = parallel_sum(data);
    std::cout << result << std::endl;
    
    return 0;
}`;

  return (
    <DocumentationLayout
      title="Solving Your First Problem"
      description="A step-by-step walkthrough of solving a Speedcode problem from start to finish."
    >
      <VStack spacing={8} align="stretch">
        {/* Introduction */}
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="semibold">Before You Start</Text>
            <Text fontSize="sm">
              This guide assumes you have basic knowledge of C++ programming. 
              We'll walk through a simple example problem to show you the Speedcode workflow.
            </Text>
          </Box>
        </Alert>

        {/* Overview */}
        <Box>
          <Heading size="lg" mb={4}>Overview</Heading>
          <Text color={textSecondary} mb={4}>
            In this guide, we'll solve a simple "Parallel Sum" problem that asks you to compute 
            the sum of a large array as quickly as possible. This is a perfect first problem because:
          </Text>
          
          <List spacing={2} pl={4}>
            <ListItem>
              <ListIcon as={CheckCircleIcon} color="green.500" />
              The algorithm is straightforward
            </ListItem>
            <ListItem>
              <ListIcon as={CheckCircleIcon} color="green.500" />
              It demonstrates parallel programming concepts
            </ListItem>
            <ListItem>
              <ListIcon as={CheckCircleIcon} color="green.500" />
              You'll see immediate performance benefits from optimization
            </ListItem>
          </List>
        </Box>

        {/* Step-by-step Guide */}
        <VStack spacing={6} align="stretch">
          <Heading size="lg">Step-by-Step Walkthrough</Heading>

          <StepCard step={1} title="Navigate to a Problem">
            <VStack spacing={4} align="start">
              <Text color={textSecondary}>
                Start by going to the <strong>Problems</strong> page from the main navigation. 
                Look for beginner-friendly problems marked with tags like "intro", "parallel", or "beginner".
              </Text>
              
              <Alert status="success" variant="left-accent">
                <AlertIcon />
                <Box>
                  <Text fontWeight="semibold">Pro Tip</Text>
                  <Text fontSize="sm">
                    Sort problems by difficulty or filter by tags to find problems suited to your skill level.
                  </Text>
                </Box>
              </Alert>
              
              <Button
                as={RouterLink}
                to="/problems"
                colorScheme="blue"
                size="sm"
                rightIcon={<ViewIcon />}
              >
                Browse Problems
              </Button>
            </VStack>
          </StepCard>

          <StepCard step={2} title="Read the Problem Description">
            <VStack spacing={4} align="start">
              <Text color={textSecondary}>
                Once you select a problem, carefully read the problem description. It will include:
              </Text>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} width="100%">
                <Card bg={accentBg} p={4}>
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="semibold">Problem Statement</Text>
                    <Text fontSize="sm">What you need to compute or optimize</Text>
                  </VStack>
                </Card>
                
                <Card bg={accentBg} p={4}>
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="semibold">Input Format</Text>
                    <Text fontSize="sm">How the input data is structured</Text>
                  </VStack>
                </Card>
                
                <Card bg={accentBg} p={4}>
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="semibold">Output Format</Text>
                    <Text fontSize="sm">What your program should output</Text>
                  </VStack>
                </Card>
                
                <Card bg={accentBg} p={4}>
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="semibold">Performance Goals</Text>
                    <Text fontSize="sm">Speed and efficiency targets</Text>
                  </VStack>
                </Card>
              </SimpleGrid>
              
              <Alert status="warning" variant="left-accent">
                <AlertIcon />
                <Box>
                  <Text fontWeight="semibold">Important</Text>
                  <Text fontSize="sm">
                    Pay special attention to input size constraints and performance requirements. 
                    These determine which optimization strategies will be most effective.
                  </Text>
                </Box>
              </Alert>
            </VStack>
          </StepCard>

          <StepCard step={3} title="Analyze the Code Editor">
            <VStack spacing={4} align="start">
              <Text color={textSecondary}>
                The problem page includes a built-in code editor with starter code. Take note of:
              </Text>
              
              <List spacing={2} pl={4}>
                <ListItem>
                  <ListIcon as={InfoIcon} color="blue.500" />
                  <strong>Template code</strong> - Basic structure and includes
                </ListItem>
                <ListItem>
                  <ListIcon as={InfoIcon} color="blue.500" />
                  <strong>Input reading</strong> - How to read the test data
                </ListItem>
                <ListItem>
                  <ListIcon as={InfoIcon} color="blue.500" />
                  <strong>Output format</strong> - Required output structure
                </ListItem>
                <ListItem>
                  <ListIcon as={InfoIcon} color="blue.500" />
                  <strong>TODO sections</strong> - Where you need to implement your solution
                </ListItem>
              </List>
              
              <Box>
                <Text fontWeight="semibold" mb={2}>Editor Features:</Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                  <HStack>
                    <Icon as={EditIcon} color="green.500" />
                    <Text fontSize="sm">Syntax highlighting</Text>
                  </HStack>
                  <HStack>
                    <Icon as={CheckCircleIcon} color="green.500" />
                    <Text fontSize="sm">Auto-completion</Text>
                  </HStack>
                  <HStack>
                    <Icon as={WarningIcon} color="orange.500" />
                    <Text fontSize="sm">Error detection</Text>
                  </HStack>
                </SimpleGrid>
              </Box>
            </VStack>
          </StepCard>

          <StepCard step={4} title="Write Your Solution">
            <VStack spacing={4} align="start">
              <Text color={textSecondary}>
                Start with a correct sequential solution, then optimize for performance. 
                Here's an example parallel sum implementation:
              </Text>
              
              <Box
                bg={codeBg}
                p={4}
                borderRadius="md"
                overflow="auto"
                width="100%"
              >
                <Code
                  display="block"
                  whiteSpace="pre"
                  fontSize="sm"
                  fontFamily="monospace"
                >
                  {exampleCode}
                </Code>
              </Box>
              
              <Alert status="info" variant="left-accent">
                <AlertIcon />
                <Box>
                  <Text fontWeight="semibold">Key Optimization Concepts</Text>
                  <List spacing={1} fontSize="sm" mt={2}>
                    <ListItem>• Use <Code>cilk_spawn</Code> for parallel execution</ListItem>
                    <ListItem>• Balance parallel overhead with work size</ListItem>
                    <ListItem>• Minimize memory allocations in hot paths</ListItem>
                    <ListItem>• Consider cache-friendly memory access patterns</ListItem>
                  </List>
                </Box>
              </Alert>
            </VStack>
          </StepCard>

          <StepCard step={5} title="Submit Your Solution">
            <VStack spacing={4} align="start">
              <Text color={textSecondary}>
                Once you're satisfied with your code, click the <strong>Submit</strong> button. 
                Your solution will be compiled and tested against multiple datasets.
              </Text>
              
              <OrderedList spacing={2} pl={4}>
                <ListItem>
                  <Text fontWeight="semibold">Compilation</Text>
                  <Text fontSize="sm" color={textSecondary}>
                    Your code is compiled with optimizations enabled
                  </Text>
                </ListItem>
                <ListItem>
                  <Text fontWeight="semibold">Correctness Testing</Text>
                  <Text fontSize="sm" color={textSecondary}>
                    Verified against expected outputs on sample inputs
                  </Text>
                </ListItem>
                <ListItem>
                  <Text fontWeight="semibold">Performance Benchmarking</Text>
                  <Text fontSize="sm" color={textSecondary}>
                    Measured runtime on large-scale test cases
                  </Text>
                </ListItem>
                <ListItem>
                  <Text fontWeight="semibold">Scoring</Text>
                  <Text fontSize="sm" color={textSecondary}>
                    Performance relative to reference implementation
                  </Text>
                </ListItem>
              </OrderedList>
              
              <Button colorScheme="green" size="sm" rightIcon={<AttachmentIcon />}>
                Submit Solution
              </Button>
            </VStack>
          </StepCard>

          <StepCard step={6} title="Interpret Your Results">
            <VStack spacing={4} align="start">
              <Text color={textSecondary}>
                After submission, you'll receive detailed feedback including:
              </Text>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} width="100%">
                <Card bg={accentBg} p={4}>
                  <VStack align="start" spacing={2}>
                    <HStack>
                      <Icon as={CheckCircleIcon} color="green.500" />
                      <Text fontWeight="semibold">Correctness Results</Text>
                    </HStack>
                    <Text fontSize="sm">
                      Pass/fail status for each test case
                    </Text>
                  </VStack>
                </Card>
                
                <Card bg={accentBg} p={4}>
                  <VStack align="start" spacing={2}>
                    <HStack>
                      <Icon as={TimeIcon} color="blue.500" />
                      <Text fontWeight="semibold">Performance Metrics</Text>
                    </HStack>
                    <Text fontSize="sm">
                      Runtime, speedup, and efficiency scores
                    </Text>
                  </VStack>
                </Card>
              </SimpleGrid>
              
              <Button
                as={RouterLink}
                to="/docs/understanding-results"
                variant="outline"
                size="sm"
                rightIcon={<ViewIcon />}
              >
                Learn About Results Interpretation
              </Button>
            </VStack>
          </StepCard>
        </VStack>

        {/* Next Steps */}
        <Box>
          <Divider mb={6} />
          <Heading size="lg" mb={4}>Next Steps</Heading>
          <VStack spacing={4} align="start">
            <Text color={textSecondary}>
              Congratulations on solving your first Speedcode problem! Here's what to explore next:
            </Text>
            
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} width="100%">
              <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" p={4}>
                <VStack align="start" spacing={2}>
                  <Text fontWeight="semibold">Try More Problems</Text>
                  <Text fontSize="sm" color={textSecondary}>
                    Practice with different algorithms and optimization challenges
                  </Text>
                  <Button as={RouterLink} to="/problems" size="xs" variant="outline">
                    Browse Problems
                  </Button>
                </VStack>
              </Card>
              
              <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" p={4}>
                <VStack align="start" spacing={2}>
                  <Text fontWeight="semibold">Learn Optimization</Text>
                  <Text fontSize="sm" color={textSecondary}>
                    Study advanced performance optimization techniques
                  </Text>
                  <Button as={RouterLink} to="/docs/optimization-strategies" size="xs" variant="outline">
                    Optimization Guide
                  </Button>
                </VStack>
              </Card>
              
              <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" p={4}>
                <VStack align="start" spacing={2}>
                  <Text fontWeight="semibold">Join Contests</Text>
                  <Text fontSize="sm" color={textSecondary}>
                    Compete in performance programming competitions
                  </Text>
                  <Button as={RouterLink} to="/contests" size="xs" variant="outline">
                    View Contests
                  </Button>
                </VStack>
              </Card>
            </SimpleGrid>
          </VStack>
        </Box>
      </VStack>
    </DocumentationLayout>
  );
};

export default FirstProblem; 